import { useRef, useState, type ReactNode } from 'react';
import cn from 'classnames';
import useClickOutside from '@/shared/hooks/useClickOutside';

// Белая пилюля с выпадающей карточкой — вид строки аккаунта telecom (AccountBar):
// баланс с кошельками и язык стоят рядом, поэтому обязаны раскрываться одинаково.
// Общий компонент, а не два набора одинаковых классов: иначе вид разъедется при
// первой же правке одного из них.
//
// Открытие, закрытие по клику снаружи и по выбору пункта — здесь; содержимое
// карточки отдаёт потребитель (ссылки у кошельков, кнопки у языков), пункты он
// красит pillDropdownItemClassName (отдельный файл — здесь только компонент).

type PillDropdownProps = {
  // Содержимое пилюли — текст и, при желании, иконка слева.
  label: ReactNode;
  // Подпись над списком (мелкие заглавные): «Кошельки», «Язык».
  heading: string;
  // Роль списка: menu — действия и ссылки, listbox — выбор значения.
  popupRole: 'menu' | 'listbox';
  // Доступное имя кнопки, если текста пилюли для него мало.
  ariaLabel?: string;
  // Пункты карточки; close — закрыть после выбора.
  children: (close: () => void) => ReactNode;
  // Фон пилюли: 'light' — белая (строка аккаунта над контентом), 'inverse' —
  // светлее винного фона (низ мобильного меню на весь экран), 'deep' — винная
  // на красном фоне (угол страниц авторизации), 'muted' — серая на белой
  // карточке (валюта в карточке заработка на дашборде).
  tone?: 'light' | 'inverse' | 'deep' | 'muted';
  // Куда раскрывается карточка: вниз или вверх (пилюля у нижнего края экрана).
  placement?: 'bottom' | 'top';
  // 'compact' — меньше поля, как пилюля баланса в верхней панели телефона.
  size?: 'default' | 'compact';
  className?: string;
};

export const PillDropdown = ({
  label,
  heading,
  popupRole,
  ariaLabel,
  children,
  tone = 'light',
  placement = 'bottom',
  size = 'default',
  className,
}: PillDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const close = () => setOpen(false);

  return (
    <div ref={ref} className={cn('relative shrink-0', className)}>
      <button
        type="button"
        aria-haspopup={popupRole}
        aria-expanded={open}
        aria-label={ariaLabel}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full text-[13px] font-semibold leading-4',
          size === 'compact' ? 'px-3 py-2' : 'px-[18px] py-2.5',
          tone === 'light' || tone === 'muted'
            ? cn(
                'text-content-secondary hover:text-content-primary',
                tone === 'light' ? 'bg-surface-card' : 'bg-surface-muted',
              )
            : cn(
                'text-content-inverse [&_b]:text-content-inverse [&_svg]:text-content-inverse',
                tone === 'deep' ? 'bg-surface-sidebar' : 'bg-surface-sidebar-hover',
              ),
        )}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role={popupRole}
          aria-label={heading}
          className={cn(
            'absolute right-0 z-50 min-w-full rounded-surface-sm bg-surface-card p-1.5 shadow-base',
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
          )}
        >
          <div
            aria-hidden
            className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-content-tertiary"
          >
            {heading}
          </div>
          {children(close)}
        </div>
      )}
    </div>
  );
};
